import {
    useGetPerformanceOffersGraphQuery,
    useGetPerformanceOffersReportExportMutation,
    useGetPerformanceOffersReportQuery,
} from "api/TenantSlug/Report/hooks";
import { useReport } from "components/Graph/hooks/useReport.ts";
import { useReportRowSelectors } from "components/Graph/hooks/useReportSelectors.ts";
import { EntityGraph } from "components/Graph/UI/EntityGraph/EntityGraph.tsx";
import { ShowInGraphTableButton } from "components/Graph/UI/Miscs/ShowInGraphTableBtn.tsx";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils.ts";
import { ALLOWED_KEYS, DEFAULT_VISIBLE_KEYS } from "components/Report/common-config.ts";
import { ReportSourceToggleButton } from "components/Report/UI/ReportSourceToggleButton/ReportSourceToggleButton.tsx";
import { StatusMultiSelect } from "components/Report/UI/StatusMultiSelect/StatusMultiSelect.tsx";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations.ts";
import { TableColumnType } from "components/UI/Table/types/table";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";
import { NumberCell } from "components/UI/Table/UI/NumberCell/NumberCell.tsx";
import { PercentCell } from "components/UI/Table/UI/PercentCell/PercentCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { generateSummary } from "components/UI/Table/utils/summary.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useReportDataSourceToggle } from "hooks/useReportDataSourceToggle.ts";
import { useUser } from "hooks/useUser";
import { Fragment, useEffect } from "react";
import { useRecoilValue } from "recoil";
import {
    PayoutResult,
    PerformanceReportDataSourceEnum,
    PerformanceReportViewEnum,
    PermissionEnum,
} from "types/enums.ts";
import { TableId } from "types/models";

import PerformanceReportSeriesResponse = App.Http.Responses.Report.PerformanceReportSeriesResponse;
import GetPerformanceReportResponse = App.Http.Responses.Report.GetPerformanceReportResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

export const OfferReport = () => {
    const userUtils = useUser();
    const tableId = TableId.OffersReportTable;
    const tableAtomInstance = tableAtom<PerformanceReportSeriesResponse>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { t } = usePaldockTranslation("common");
    const { t: tR } = usePaldockTranslation("ui.report.columns");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const exportState = useRecoilValue(exportAtom(tableId));
    const { actualSource, actualStatuses, toggleSource, toggleStatuses } = useReportDataSourceToggle(
        PerformanceReportDataSourceEnum.ReportPayoutPerformances,
        [PayoutResult.Approved]
    );

    const { sortBy, setSortBy } = useReport("offer-graph", "count", TableId.OffersReportTable);

    if (!userUtils.isAdmin() && !userUtils.isAffiliate()) {
        // TODO to refactor after BE multiroles refactor
        throw new Error("Reports for other roles than admin or affiliate not implemented.");
    }

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                payout_result: {
                    in: actualStatuses,
                },
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.datetime_at
                    ? {}
                    : {
                          datetime_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? `-${sortBy}`,
            include: "offer",
        },
        view: userUtils.isAdmin() ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
        source: actualSource,
    };

    const {
        data: graphData,
        isLoading: graphLoading,
        isError: graphError,
    } = useGetPerformanceOffersGraphQuery({
        filter: {
            filter: {
                payout_result: {
                    in: actualStatuses,
                },
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.datetime_at
                    ? {}
                    : {
                          datetime_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            include: "offer",
        },
        view: userUtils.isAdmin() ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
        source: actualSource,
    });

    const query = useGetPerformanceOffersReportQuery(request, {
        enabled: false,
    });

    const { selectedRows, isSelectionOn, setIsSelectionOn, defaultSelection } = useReportRowSelectors(
        Object.values(graphData?.timeline || {}).flatMap((item) => Object.values(item)),
        (record) => record?.offer?.id,
        sortBy
    );

    const getPerformanceOffersReportExportMutation = useGetPerformanceOffersReportExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetPerformanceReportResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, getPerformanceOffersReportExportMutation);

    useEffect(() => {
        tableFetchDataUtils.setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: {
                filter: {
                    ...(prev.filter?.filter || {}),
                    payout_result: {
                        in: actualStatuses,
                    },
                },
            },
            source: actualSource,
        }));
    }, [actualStatuses, actualSource]);

    const extraFilters = [
        {
            dataIndex: "owner.nickname",
            title: tG("affiliate"),
            filterType: FILTER_TYPES.partnerSelect,
            filterName: "owner_id",
        },
        {
            dataIndex: "advertiser.nickname",
            title: tG("advertiser"),
            filterType: FILTER_TYPES.advertiserSelect,
            filterName: "advertiser_id",
        },
    ];

    const columnConfig: TableColumnType<PerformanceReportSeriesResponse>[] = [
        TableColumn.Entity.Offer<PerformanceReportSeriesResponse>({
            title: tG("offer"),
            dataIndex: "offer_id",
            sorter: false,
            width: 150,
            render: (_, record) => <EntityLinkCell.OfferReport entity={record.offer} />,
            getEntity: (record) => record.offer,
        }),
        {
            title: tR("clicks"),
            dataIndex: "click",
            ellipsis: true,
            key: "click",
            render: (click) => <NumberCell amount={click} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("leads"),
            dataIndex: "lead",
            ellipsis: true,
            key: "lead",
            render: (lead) => <NumberCell amount={lead} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("transactions"),
            dataIndex: "count",
            ellipsis: true,
            key: "count",
            render: (count) => <NumberCell amount={count} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("cpl"),
            dataIndex: "cpl",
            ellipsis: true,
            key: "cpl",
            render: (_, record) => <MoneyCell money={record.cpl} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "cpl",
            sorter: true,
            defaultSortOrder: "descend",
            width: 130,
        },
        ...(userUtils.isAdmin()
            ? [
                  {
                      title: tR("cost"),
                      dataIndex: "cost",
                      ellipsis: true,
                      key: "cost",
                      render: (_: string, record: PerformanceReportSeriesResponse) => <MoneyCell money={record.cost} />,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "cost",
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),
        ...(userUtils.isAdmin()
            ? [
                  {
                      title: tR("epl"),
                      dataIndex: "epl",
                      ellipsis: true,
                      key: "epl",
                      render: (_: string, record: PerformanceReportSeriesResponse) => <MoneyCell money={record.epl} />,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "epl",
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),
        {
            title: tR("revenue"),
            dataIndex: "revenue",
            ellipsis: true,
            key: "revenue",
            render: (_, record) => <MoneyCell money={record.revenue} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "revenue",
            sorter: true,
            defaultSortOrder: "descend",
            width: 130,
        },
        ...(userUtils.isAdmin()
            ? [
                  {
                      title: tR("profit"),
                      dataIndex: "profit",
                      ellipsis: true,
                      key: "profit",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <MoneyCell money={record.profit} />
                      ),
                      filterName: "profit",
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),
        ...(userUtils.isAdmin()
            ? [
                  {
                      title: tR("roi"),
                      dataIndex: "roi",
                      ellipsis: true,
                      key: "roi",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <PercentCell amount={record.roi} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 100,
                  },
              ]
            : []),
        ...(userUtils.isAdmin()
            ? [
                  {
                      title: tR("margin"),
                      dataIndex: "margin",
                      ellipsis: true,
                      key: "margin",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <PercentCell amount={record.margin} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 100,
                  },
              ]
            : []),
    ];

    const isAdmin = userUtils.isAdmin();
    const summaryKeys = isAdmin
        ? ["click", "lead", "count", "cpl", "cost", "epl", "revenue", "profit", "roi", "margin"]
        : ["click", "lead", "count", "cpl", "revenue"];

    const percentKeys = isAdmin ? ["roi", "margin"] : [];

    return (
        <Fragment>
            {permissionsUtils.hasPermission(PermissionEnum.ReportShowInGraph) && (
                <EntityGraph
                    data={GeneralGraphUtils.prepareEntityData(graphData?.timeline)}
                    entity={"offer"}
                    entityId={"offer.id"}
                    graphId={"offer-graph"}
                    defaultActiveKey={"count"}
                    defaultVisibleKeys={DEFAULT_VISIBLE_KEYS(isAdmin)}
                    keys={ALLOWED_KEYS(isAdmin)}
                    loading={graphLoading}
                    error={graphError}
                    onActiveHighlightChange={(key) => setSortBy(key)}
                    isSelectionOn={isSelectionOn}
                    defaultSelectedEntities={defaultSelection}
                    selectedEntities={selectedRows}
                    summaryData={tableFetchDataUtils.tableData?.summary}
                />
            )}
            <div className={"mt-4"}>
                <Table<GetPerformanceReportResponse, PerformanceReportSeriesResponse>
                    fixFirstColumn
                    tableId={tableId}
                    rowSelection={undefined}
                    data={tableFetchDataUtils.tableData as GetPerformanceReportResponse}
                    dataSource={tableFetchDataUtils.tableData?.series || []}
                    columnConfig={columnConfig}
                    loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                    onRefresh={tableFetchDataUtils.refetchData}
                    showRefetchButton={permissionsUtils.hasPermission(PermissionEnum.ReportUpdate)}
                    enableFilters={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilter)}
                    enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilterTime)}
                    summary={(visibleColumns) =>
                        generateSummary(
                            tableFetchDataUtils.tableData?.summary,
                            summaryKeys,
                            percentKeys,
                            t("total"),
                            visibleColumns ?? undefined,
                            columnConfig,
                            false
                        )
                    }
                    autoScrollWidth={true}
                    dateFilterDataIndex={"datetime_at"}
                    extraFilters={extraFilters}
                    onExport={() =>
                        tableFetchDataUtils.exportData({
                            extension: exportState.extension,
                            headers: exportState.headers,
                            export_all: exportState.range === "all",
                            page: request.page,
                            filter: request.filter,
                            delimiter: exportState.delimiter,
                        })
                    }
                    extraTopBarElements={
                        <StatusMultiSelect onChange={toggleStatuses} defaultValue={[PayoutResult.Approved]} />
                    }
                    extraTopBarIcons={[
                        <ReportSourceToggleButton
                            key="report-source-toggle"
                            actualSource={actualSource}
                            onToggle={toggleSource}
                        />,
                        <ShowInGraphTableButton
                            key="show-in-graph"
                            isOn={isSelectionOn}
                            onIsOnChange={setIsSelectionOn}
                        />,
                    ]}
                />
            </div>
        </Fragment>
    );
};
